import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Box,
  Typography,
  Avatar,
  Button,
  Tabs,
  Tab,
  Divider,
  Card,
  CardContent,
  Chip,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  TextField,
  CircularProgress,
  Snackbar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Edit as EditIcon,
  LocationOn as LocationIcon,
  Link as LinkIcon,
  GitHub as GitHubIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  CalendarToday as CalendarIcon,
  WorkOutline as WorkIcon,
  School as SchoolIcon,
  DevicesOther as DeviceIcon,
  Security as SecurityIcon,
  BugReport as BugIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Timeline as TimelineIcon,
  Speed as SpeedIcon,
  PhotoCamera,
  Save as SaveIcon,
  Cancel as CancelIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Scanner as ScannerIcon,
  ShoppingCart as ShoppingCartIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { useNavigate } from 'react-router-dom';
import EditProfileForm from '../pages/EditProfileForm';
import Nav1 from '../components/Nav1';
import Footer from '../components/Footer';
import { v4 as uuidv4 } from 'uuid';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(3)
}));

const StatBox = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  backgroundColor: theme.palette.background.default
}));

const ProfileSection = styled(StyledPaper)(({ theme }) => ({
  transition: 'transform 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4]
  }
}));

const StatCard = styled(Card)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contralightText,
  transition: 'transform 0.2s',
  '&:hover': {
    transform: 'scale(1.05)',
  }
}));

const Profile = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userStats, setUserStats] = useState({
    posts: 0,
    comments: 0,
    likes: 0
  });
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [scanStats, setScanStats] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [selectedTab, setSelectedTab] = useState(0);
  const [activityData, setActivityData] = useState({
    questions: [],
    comments: [],
    scans: [],
    orders: []
  });
  
  const [formData, setFormData] = useState({
    username: '',
    full_name: '',
    bio: '',
    location: '',
    company: '',
    website: '',
    social_links: {
      github: '',
      twitter: '',
      linkedin: ''
    }
  });

  useEffect(() => {
    if (user) {
      fetchProfile();
      fetchUserStats();
      fetchScanStats();
      fetchActivityData();
    }
  }, [user]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('profiles2')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // Profile doesn't exist, create it
          await createInitialProfile();
        } else {
          throw error;
        }
      } else {
        setProfile(data);
        setFormData({
          username: data.username || '',
          full_name: data.full_name || '',
          bio: data.bio || '',
          location: data.location || '',
          company: data.company || '',
          website: data.website || '',
          social_links: data.social_links || {
            github: '',
            twitter: '',
            linkedin: ''
          }
        });
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError(err.message);
      showSnackbar('Error loading profile', 'error');
    } finally {
      setLoading(false);
    }
  };

  const createInitialProfile = async () => {
    try {
      const { data, error } = await supabase.rpc('create_new_profile', {
        user_id: user.id,
        user_email: user.email,
        user_name: user.email.split('@')[0]
      });

      if (error) throw error;

      await fetchProfile(); // Refresh profile data
    } catch (err) {
      console.error('Error creating profile:', err);
      setError(err.message);
      showSnackbar('Error creating profile', 'error');
    }
  };

  const fetchUserStats = async () => {
    try {
      // Fetch posts count
      const { data: postsData, error: postsError } = await supabase
        .from('posts')
        .select('*', { count: 'exact' })
        .eq('user_id', user.id);

      // Fetch comments count
      const { data: commentsData, error: commentsError } = await supabase
        .from('comments')
        .select('*', { count: 'exact' })
        .eq('user_id', user.id);

      // Fetch likes count
      const { data: likesData, error: likesError } = await supabase
        .from('likes')
        .select('*', { count: 'exact' })
        .eq('user_id', user.id);

      if (postsError || commentsError || likesError) {
        throw new Error('Error fetching stats');
      }

      setUserStats({
        posts: postsData?.length || 0,
        comments: commentsData?.length || 0,
        likes: likesData?.length || 0
      });
    } catch (err) {
      console.error('Error fetching user stats:', err);
    }
  };

  const handleProfileUpdate = (updatedProfile) => {
    setProfile(updatedProfile);
  };

  const handleEditClick = () => {
    setIsEditOpen(true);
  };

  const handleImageUpload = async (event) => {
    try {
      const file = event.target.files[0];
      if (!file) return;

      // Validate file
      if (file.size > 2 * 1024 * 1024) {
        throw new Error('File size must be less than 2MB');
      }

      if (!file.type.startsWith('image/')) {
        throw new Error('File must be an image');
      }

      setUploadingImage(true);
      setError(null);

      // Generate unique file name
      const fileExt = file.name.split('.').pop();
      const fileName = `${uuidv4()}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;

      // Remove old avatar if exists
      if (profile?.avatar_url) {
        const oldFilePath = profile.avatar_url.split('/').slice(-2).join('/');
        await supabase.storage
          .from('avatars')
          .remove([oldFilePath]);
      }

      // Upload new avatar
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      // Update profile with new avatar URL
      const { data, error: updateError } = await supabase
        .from('profiles2')
        .update({ avatar_url: publicUrl })
        .eq('id', user.id)
        .select()
        .single();

      if (updateError) throw updateError;

      setProfile(data);
      showSnackbar('Profile picture updated successfully', 'success');
    } catch (err) {
      console.error('Error uploading image:', err);
      setError(err.message);
      showSnackbar(err.message, 'error');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('profiles2')
        .update({
          ...formData,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id)
        .select()
        .single();

      if (error) throw error;

      setProfile(data);
      setIsEditing(false);
      showSnackbar('Profile updated successfully', 'success');
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err.message);
      showSnackbar('Error updating profile', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchScanStats = async () => {
    try {
      // Get the authenticated user's session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('No authenticated session');
      }

      // Fetch profile data with the user's ID
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();

      if (error) throw error;
      setScanStats(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching profile:', error.message);
      setError(error.message);
      setLoading(false);
    }
  };

  const fetchActivityData = async () => {
    try {
      // Fetch questions
      const { data: questions } = await supabase
        .from('questions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(5);

      // Fetch comments
      const { data: comments } = await supabase
        .from('comments')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(5);

      // Fetch scans
      const { data: scans } = await supabase
        .from('scans')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(5);

      // Fetch orders
      const { data: orders } = await supabase
        .from('orders')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(5);

      setActivityData({
        questions: questions || [],
        comments: comments || [],
        scans: scans || [],
        orders: orders || []
      });
    } catch (error) {
      console.error('Error fetching activity data:', error);
      showSnackbar('Error loading activity data', 'error');
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading && !profile) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <StyledPaper>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                position: 'relative' 
              }}>
                <Box sx={{ position: 'relative' }}>
                  <Avatar
                    src={profile?.avatar_url}
                    sx={{ 
                      width: 120, 
                      height: 120,
                      border: '4px solid #fff',
                      boxShadow: 1
                    }}
                  />
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="avatar-upload"
                    type="file"
                    onChange={handleImageUpload}
                    disabled={uploadingImage}
                  />
                  <label htmlFor="avatar-upload">
                    <IconButton
                      component="span"
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        backgroundColor: 'primary.main',
                        color: 'white',
                        '&:hover': { backgroundColor: 'primary.dark' }
                      }}
                      disabled={uploadingImage}
                    >
                      {uploadingImage ? <CircularProgress size={24} /> : <PhotoCamera />}
                    </IconButton>
                  </label>
                </Box>

                <Box sx={{ ml: 3, flex: 1 }}>
                  {isEditing ? (
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Full Name"
                          value={formData.full_name}
                          onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Username"
                          value={formData.username}
                          onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                        />
                      </Grid>
                    </Grid>
                  ) : (
                    <>
                      <Typography variant="h4">
                        {profile?.full_name || 'Anonymous'}
                      </Typography>
                      <Typography variant="subtitle1" color="text.secondary">
                        @{profile?.username}
                      </Typography>
                    </>
                  )}
                </Box>

                <Box>
                  {isEditing ? (
                    <>
                      <IconButton onClick={handleSave} color="primary" disabled={loading}>
                        <SaveIcon />
                      </IconButton>
                      <IconButton onClick={() => setIsEditing(false)} color="error">
                        <CancelIcon />
                      </IconButton>
                    </>
                  ) : (
                    <IconButton onClick={() => setIsEditing(true)} color="primary">
                      <EditIcon />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </StyledPaper>
          </Grid>

          {/* Enhanced Profile Details */}
          <Grid item xs={12} md={8}>
            <StyledPaper>
              <Typography variant="h6" gutterBottom>
                About
              </Typography>
              {isEditing ? (
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Bio"
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  sx={{ mb: 3 }}
                />
              ) : (
                <Typography paragraph>
                  {profile?.bio || 'No bio added yet'}
                </Typography>
              )}

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Social Links
              </Typography>
              {isEditing ? (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="GitHub"
                      value={formData.social_links.github}
                      onChange={(e) => setFormData({
                        ...formData,
                        social_links: {
                          ...formData.social_links,
                          github: e.target.value
                        }
                      })}
                      InputProps={{
                        startAdornment: <GitHubIcon sx={{ mr: 1 }} />
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Twitter"
                      value={formData.social_links.twitter}
                      onChange={(e) => setFormData({
                        ...formData,
                        social_links: {
                          ...formData.social_links,
                          twitter: e.target.value
                        }
                      })}
                      InputProps={{
                        startAdornment: <TwitterIcon sx={{ mr: 1 }} />
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="LinkedIn"
                      value={formData.social_links.linkedin}
                      onChange={(e) => setFormData({
                        ...formData,
                        social_links: {
                          ...formData.social_links,
                          linkedin: e.target.value
                        }
                      })}
                      InputProps={{
                        startAdornment: <LinkedInIcon sx={{ mr: 1 }} />
                      }}
                    />
                  </Grid>
                </Grid>
              ) : (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  {profile?.social_links?.github && (
                    <IconButton 
                      component="a" 
                      href={profile.social_links.github}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <GitHubIcon />
                    </IconButton>
                  )}
                  {profile?.social_links?.twitter && (
                    <IconButton 
                      component="a" 
                      href={profile.social_links.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <TwitterIcon />
                    </IconButton>
                  )}
                  {profile?.social_links?.linkedin && (
                    <IconButton 
                      component="a" 
                      href={profile.social_links.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <LinkedInIcon />
                    </IconButton>
                  )}
                </Box>
              )}
            </StyledPaper>
          </Grid>

          {/* Right Sidebar */}
          <Grid item xs={12} md={4}>
            <StyledPaper>
              <Typography variant="h6" gutterBottom>
                Details
              </Typography>
              {isEditing ? (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Location"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      InputProps={{
                        startAdornment: <LocationIcon sx={{ mr: 1 }} />
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Company"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      InputProps={{
                        startAdornment: <WorkIcon sx={{ mr: 1 }} />
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Website"
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      InputProps={{
                        startAdornment: <LinkIcon sx={{ mr: 1 }} />
                      }}
                    />
                  </Grid>
                </Grid>
              ) : (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <LocationIcon sx={{ mr: 1 }} />
                    <Typography variant="body2">
                      {profile?.location || 'No location specified'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <WorkIcon sx={{ mr: 1 }} />
                    <Typography variant="body2">
                      {profile?.company || 'No company specified'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <LinkIcon sx={{ mr: 1 }} />
                    <Typography variant="body2">
                      {profile?.website || 'No website specified'}
                    </Typography>
                  </Box>
                </Box>
              )}
            </StyledPaper>
          </Grid>
        </Grid>
      </Paper>

      {/* Activity Tabs */}
      <Box sx={{ mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={(e, newValue) => setSelectedTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<TimelineIcon />} label="Overview" />
          <Tab icon={<QuestionAnswerIcon />} label="Questions" />
          <Tab icon={<ScannerIcon />} label="Scans" />
          <Tab icon={<ShoppingCartIcon />} label="Orders" />
        </Tabs>
      </Box>

      {/* Stats Overview */}
      {selectedTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <StatCard>
              <CardContent>
                <Typography variant="h6">Questions</Typography>
                <Typography variant="h3">{activityData.questions.length}</Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={(activityData.questions.length / 100) * 100} 
                  sx={{ mt: 2 }}
                />
              </CardContent>
            </StatCard>
          </Grid>
          {/* Similar StatCards for Comments, Scans, and Orders */}
        </Grid>
      )}

      {/* Questions Tab */}
      {selectedTab === 1 && (
        <ProfileSection>
          <Typography variant="h6" gutterBottom>
            Recent Questions
          </Typography>
          <List>
            {activityData.questions.map((question) => (
              <ListItem key={question.id}>
                <ListItemIcon>
                  <QuestionAnswerIcon />
                </ListItemIcon>
                <ListItemText
                  primary={question.title}
                  secondary={new Date(question.created_at).toLocaleDateString()}
                />
                <Chip 
                  label={question.status} 
                  color={question.status === 'answered' ? 'success' : 'warning'}
                />
              </ListItem>
            ))}
          </List>
        </ProfileSection>
      )}

      {/* Similar sections for Comments, Scans, and Orders tabs */}

      {/* Achievement Section */}
      <ProfileSection sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Achievements
        </Typography>
        <Grid container spacing={2}>
          {[1, 2, 3].map((achievement) => (
            <Grid item xs={12} sm={4} key={achievement}>
              <Card sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                <StarIcon color="primary" sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="subtitle1">Achievement {achievement}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Unlocked on {new Date().toLocaleDateString()}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </ProfileSection>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Profile; 